<template>
	<App title="Region Management">
		<div class="w-100 position-relative">
			<Block class="border-top border-secondary border-2 col-md-10 mx-auto">
				<template #header>
					<h3 class="block-title">Region Management</h3>
				</template>

				<!-- Region Management Form -->
				<div class="row">
					<div class="col-12">
						<div class="card border-0 ">
							<div class="card-header bg-light border-0">
								<h5 class="card-title mb-0">
									<i class="bi bi-geo-alt-fill text-secondary me-2"></i>
									Create New Region
								</h5>
							</div>
							<div class="card-body p-4">
								<Form ref="regionForm" @submit="onRegionSubmit">
									<div class="row g-3">
										<!-- Country Selection -->
										<div class="col-12 col-md-6">
											<label for="regionCountry" class="form-label text-dark fw-normal">
												<i class="bi bi-flag me-1"></i>Country
											</label>
											<SelectField 
												id="regionCountry" 
												v-model="regionData.country" 
												name="country" 
												rules="required" 
												label="Country" 
												:options="country_options"
											>

											</SelectField>
										</div>

										<!-- Region Name -->
										<div class="col-12 col-md-6">
											<label for="regionName" class="form-label text-dark fw-normal">
												<i class="bi bi-geo-alt me-1"></i>Region Name
											</label>
											<InputField 
												id="region" 
												v-model="regionData.region" 
												name="region" 
												rules="required|min:3|max:50" 
												label="Region Name" 
												placeholder="Enter region name..." 
											/>
										</div>
										<div class="col-12 ">
											<label for="zoneManager" class="form-label text-dark fw-normal">
												<i class="bi bi-person-badge me-1"></i>Manager (Optional)
											</label>
											<SelectField 
												id="zoneManager" 
												v-model="regionData.manager" 
												name="manager" 
												label="Manager" 
												:options="manager_options"
											>
											
											</SelectField>
										</div>
										

										<!-- Form Buttons -->
										<div class="col-12">
											<div class="d-grid gap-2 d-md-flex justify-content-md-end pt-3">
												<button 
													type="button" 
													class="btn bg-danger text-white me-2 px-4" 
													:disabled="regionLoading || regionDone" 
													@click="clearRegionForm"
												>
													<i class="bi bi-x-circle me-1"></i>Clear
												</button>
												<Button 
													id="regionAction" 
													type="submit" 
													:label="regionLabels" 
													:loading="regionLoading" 
													:done="regionDone" 
													class="btn bg-secondary text-light px-4" 
													:disabled="checkRegionInput" 
												/>
											</div>
										</div>
									</div>
								</Form>
							</div>
						</div>
					</div>
				</div>
			</Block>

			<!-- Notification Area -->
			<div class="mt-9 position-absolute top-0 end-0">
				<NotivueKeyboard v-slot="{ containersTabIndex }">
					<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
						<AppNotification :item="item" />
					</Notivue>
				</NotivueKeyboard>
			</div>
		</div>
	</App>
</template>

<script lang="ts">
import $ from 'jquery'
import { useConfirm } from '@/libs/useConfirm.ts'
import locations from '@/json/location.json'
import Swal from 'sweetalert2'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import { Component, Ref, Vue, toNative } from 'vue-facing-decorator'
import { Notivue, push, materialTheme, NotivueKeyboard } from 'notivue'
import AppNotification from '@/vue/components/AppNotification.vue'



@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		Button,
		Block,
		SelectField,
		TextareaField,
		Notivue,
		AppNotification,
		NotivueKeyboard
	},
})
class RegionManagement extends Vue {
	confirm = useConfirm().confirm
	materialTheme = materialTheme

	// Region Form Data
	regionLoading = false
	regionDone = false
	regionData = {
		country: '',
		region: '',
		manager: '',
		
	}

	
	@Ref('regionForm')
	readonly regionForm!: InstanceType<typeof Form>

	// Computed Properties
	get country_options(): Select2Options {
		return {
			placeholder: 'Select Country',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('country.ajax.select.country'),
			},

			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			
		}
	}
	get manager_options(): Select2Options {
		return {
			
			placeholder: 'Select Manager',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('admin.ajax.select.admin'),
			},

			//@ts-ignore
			templateResult: ({ id, fullname,email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, fullname,email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
		}
	}
	get regionLabels(): ButtonLabel {
		return {
			normal: 'Create Region',
			loading: 'Creating...',
			done: 'Created'
		}
	}

	get checkRegionInput(): boolean {
		return [this.regionData.country, this.regionData.region].some(field => field.trim() === '')
	}

	// Methods
	clearRegionForm() {
		this.regionData = {
			country: '',
			region: '',
			manager:''
			
		}
		this.regionForm.reset()
	}

	
	async onRegionSubmit(payload: any) {
	
		this.regionLoading = true
		this.$http
			.post(this.route('region.page.create'), payload)
			.then((response) => {
				Swal.fire({
					title: 'Success!',
					text: 'Region created successfully!',
					icon: 'success',
					confirmButtonText: 'OK'
				}).then(() => {
					this.clearRegionForm()
					this.regionDone = false
					setTimeout(()=>{
						this.router.visit(this.route('region.page.group'))
					},3000)
				
				})	
			})
			.catch((e) => {
				push.error({
					title: 'Error',
					message: 'Unable to create zone at this time. Please try again later.'
				})
				
			})
			.finally(() => (this.regionLoading = false))
	}
}

export default toNative(RegionManagement)
</script>